import { useMutation } from '@apollo/client'
import { SnackbarVariant } from '@focaldata/cin-ui-components'
import { SET_BASE_MARKET_V2 } from '../../data/gql/questionnaire/mutations/setBaseMarketV2'
import { LoggerErrorType } from '../../data/logger'
import { captureApolloError } from '../../utils/HelperFunctions'
import { useOptionalWaveId } from '../useOptionalWaveId'
import { useProjectId } from '../useProjectId'
import { useSafeEnqueueSnackbar } from '../useSafeEnqueueSnackbar'
import { evictAudienceCaches } from './evictAudienceCaches'

/**
 * Sets a market as the project's base market. `setBaseMarket(surveyId, true)` is
 * an atomic swap: the backend sets isBase=true on this market and isBase=false
 * on every other market (demoting the previous base) in a single aggregate
 * write, then re-propagates the global questions from the new base. Callers must
 * therefore make ONE call — a second concurrent write (e.g. resetting the old
 * base) loses the optimistic-lock race. `isBase=false` is only for removing the
 * base entirely, never for a swap.
 *
 * The `surveyId` identifies the market being changed and is passed per call
 * (this runs on the project screen, which has no current-survey context), while
 * `projectId` comes from the route. Refetching the marketsSpecification query is
 * left to the caller so a single refresh follows the swap.
 */
export const useSetBaseMarketV2 = () => {
  const projectId = useProjectId()
  const waveId = useOptionalWaveId()
  const enqueueSnackbar = useSafeEnqueueSnackbar()

  const [setBaseMarketV2Mutation] = useMutation(SET_BASE_MARKET_V2, {
    context: { clientName: 'questionnaire' },
    onError(error) {
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'setBaseMarketV2',
        error
      )
      enqueueSnackbar?.('Could not update the base market. Please try again.', {
        variant: SnackbarVariant.Error
      })
    },
    // Changing the base re-propagates global questions across markets; evict the
    // (unmounted) per-market audience caches so they refetch when next opened.
    update: (cache) => evictAudienceCaches(cache)
  })

  const setBaseMarket = (surveyId: string, isBase: boolean) =>
    setBaseMarketV2Mutation({
      variables: {
        input: {
          projectId,
          surveyId,
          waveId,
          isBase
        }
      }
    })

  return setBaseMarket
}
